import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  Lock, Mail, User, Eye, EyeOff, ArrowRight, Sparkles, Shield,
  Check, KeyRound, ArrowLeft, Smartphone, Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  const [tab, setTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteData, setInviteData] = useState(null);

  const [form, setForm] = useState({
    email: '',
    password: '',
    name: ''
  });

  // Password reset state
  const [resetStep, setResetStep] = useState(null); // null | 'request' | 'verify'
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetMethod, setResetMethod] = useState('email');
  const [debugCode, setDebugCode] = useState(null);

  // Check for invite token
  const inviteToken = searchParams.get('invite');
  useEffect(() => {
    if (inviteToken) {
      axios.get(`${API}/invites/validate/${inviteToken}`).then(res => {
        setInviteData(res.data);
        setForm(prev => ({ ...prev, name: res.data.name, email: res.data.email }));
        setTab('invite');
      }).catch(() => toast.error('Invitación no válida o expirada'));
    }
  }, [inviteToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return;
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error de autenticación');
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return;
    if (form.password.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      // New users go to onboarding
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar');
    } finally { setLoading(false); }
  };

  const handleAcceptInvite = async (e) => {
    e.preventDefault();
    if (!form.password || form.password.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/invites/accept`, { invite_token: inviteToken, password: form.password });
      localStorage.setItem('deal-token', res.data.token);
      toast.success('Cuenta creada con éxito');
      window.location.href = '/';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aceptar invitación');
    } finally { setLoading(false); }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!resetEmail) { toast.error('Ingresa tu correo electrónico'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/password-reset/request`, { email: resetEmail, method: resetMethod });
      if (res.data.debug_code) setDebugCode(res.data.debug_code);
      toast.success('Código de recuperación enviado');
      setResetStep('verify');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al solicitar código');
    } finally { setLoading(false); }
  };

  const handleVerifyReset = async (e) => {
    e.preventDefault();
    if (!resetCode || !resetNewPassword) { toast.error('Completa todos los campos'); return; }
    if (resetNewPassword.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/verify`, { email: resetEmail, code: resetCode, new_password: resetNewPassword });
      toast.success('Contraseña actualizada. Ahora puedes iniciar sesión.');
      setResetStep(null);
      setResetEmail('');
      setResetCode('');
      setResetNewPassword('');
      setDebugCode(null);
      setTab('login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código inválido o expirado');
    } finally { setLoading(false); }
  };

  const roleLabels = { manager: 'Manager de Ventas', monitor: 'Monitor de Ventas' };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#002d2c]">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-brand-green/25 rounded-full blur-3xl" />
          <div className="absolute bottom-32 right-20 w-96 h-96 bg-brand-copper/15 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-brand-butter/10 rounded-full blur-2xl" />
        </div>
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="flex items-center gap-3 mb-8">
            <img src="/icon-512.png" alt="Noah" className="w-14 h-14 rounded-2xl shadow-xl shadow-brand-green/30" />
            <div>
              <h1 className="text-3xl font-bold tracking-tight">DEAL APP</h1>
              <p className="text-sm text-brand-butter/80">La IA que convierte conversaciones en oportunidades</p>
            </div>
          </div>
          <h2 className="text-4xl font-bold mb-4 leading-tight">
            Gestiona tus ventas<br />
            <span className="text-brand-butter">con inteligencia artificial</span>
          </h2>
          <p className="text-lg text-white/75 mb-8 max-w-md">
            Noah IA responde a tus clientes, organiza tu pipeline y cierra ventas por ti. Todo en una sola plataforma.
          </p>
          <div className="space-y-4">
            {['Noah IA cierra ventas 24/7', 'Pipeline visual con burbujas interactivas', 'Pagos seguros con sistema Escrow', 'Analytics en tiempo real por campaña'].map((f) => (
              <div key={f} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-brand-butter/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-brand-butter" />
                </div>
                <span className="text-sm text-white/85">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src="/icon-512.png" alt="Noah" className="w-12 h-12 rounded-2xl" />
            <h1 className="text-2xl font-bold">DEAL APP</h1>
          </div>

          {inviteData ? (
            /* Invite Accept Form */
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="border-violet-500/20">
                <CardContent className="p-6 space-y-6">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
                    </div>
                    <h2 className="text-xl font-bold">Invitación Recibida</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      <span className="font-medium">{inviteData.invited_by_name}</span> te invitó como
                    </p>
                    <Badge className="mt-2 bg-violet-500/10 text-brand-deep dark:text-brand-deep dark:text-brand-green border-violet-500/30">
                      {roleLabels[inviteData.role] || inviteData.role}
                    </Badge>
                  </div>
                  <form onSubmit={handleAcceptInvite} className="space-y-4">
                    <div>
                      <Label>Nombre</Label>
                      <Input value={form.name} disabled className="bg-muted" />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input value={form.email} disabled className="bg-muted" />
                    </div>
                    <div>
                      <Label>Crear contraseña *</Label>
                      <div className="relative">
                        <Input
                          data-testid="invite-password"
                          type={showPassword ? 'text' : 'password'}
                          value={form.password}
                          onChange={e => setForm({...form, password: e.target.value})}
                          placeholder="Mínimo 6 caracteres"
                        />
                        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button data-testid="accept-invite-btn" type="submit" className="w-full bg-primary" disabled={loading}>
                      {loading ? 'Procesando...' : 'Aceptar Invitación'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            /* Login / Register */
            <div className="relative">
              <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList className="w-full mb-6">
                  <TabsTrigger value="login" className="flex-1" data-testid="tab-login">Iniciar Sesión</TabsTrigger>
                  <TabsTrigger value="register" className="flex-1" data-testid="tab-register">Crear Cuenta</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <Label>Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            data-testid="login-email"
                            type="email"
                            value={form.email}
                            onChange={e => setForm({...form, email: e.target.value})}
                            className="pl-10"
                            placeholder="tu@email.com"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Contraseña</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            data-testid="login-password"
                            type={showPassword ? 'text' : 'password'}
                            value={form.password}
                            onChange={e => setForm({...form, password: e.target.value})}
                            className="pl-10 pr-10"
                            placeholder="Tu contraseña"
                          />
                          <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPassword(!showPassword)}>
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-end">
                        <button
                          type="button"
                          data-testid="forgot-password-link"
                          className="text-sm text-brand-deep dark:text-brand-deep dark:text-brand-green hover:text-brand-deep dark:text-brand-deep dark:text-brand-green transition-colors"
                          onClick={() => { setResetStep('request'); setResetEmail(form.email); }}
                        >
                          Olvide mi contraseña
                        </button>
                      </div>
                      <Button data-testid="login-submit" type="submit" className="w-full gap-2 bg-primary hover:opacity-90 h-11" disabled={loading}>
                        {loading ? 'Ingresando...' : 'Iniciar Sesión'}
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </form>
                  </motion.div>
                </TabsContent>

                <TabsContent value="register">
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <div className="mb-4 p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
                      <div className="flex items-center gap-2 text-sm">
                        <Crown className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
                        <span className="font-medium text-brand-deep dark:text-brand-deep dark:text-brand-green dark:text-brand-deep dark:text-brand-deep dark:text-brand-green">Cuenta Administrador</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Al registrarte creas una cuenta de administrador. Podrás invitar Managers y Monitores después.
                      </p>
                    </div>
                    <form onSubmit={handleRegister} className="space-y-4">
                      <div>
                        <Label>Nombre completo</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            data-testid="register-name"
                            value={form.name}
                            onChange={e => setForm({...form, name: e.target.value})}
                            className="pl-10"
                            placeholder="Tu nombre"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            data-testid="register-email"
                            type="email"
                            value={form.email}
                            onChange={e => setForm({...form, email: e.target.value})}
                            className="pl-10"
                            placeholder="tu@empresa.com"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Contraseña</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            data-testid="register-password"
                            type={showPassword ? 'text' : 'password'}
                            value={form.password}
                            onChange={e => setForm({...form, password: e.target.value})}
                            className="pl-10 pr-10"
                            placeholder="Mínimo 6 caracteres"
                          />
                          <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPassword(!showPassword)}>
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <Button data-testid="register-submit" type="submit" className="w-full gap-2 bg-primary hover:opacity-90 h-11" disabled={loading}>
                        {loading ? 'Creando cuenta...' : 'Crear Cuenta de Administrador'}
                        <Shield className="w-4 h-4" />
                      </Button>
                      <p className="text-xs text-center text-muted-foreground">
                        Prueba gratis 14 días. Explora los planes después de registrarte.
                      </p>
                    </form>
                  </motion.div>
                </TabsContent>
              </Tabs>

              <p className="text-center text-[11px] text-muted-foreground mt-4 flex flex-wrap justify-center gap-x-3 gap-y-1" data-testid="login-legal-links">
                <Link to="/privacidad" className="hover:underline" data-testid="login-privacy-link">Política de Privacidad</Link>
                <span>·</span>
                <Link to="/terminos" className="hover:underline" data-testid="login-terms-link">Términos</Link>
                <span>·</span>
                <Link to="/eliminar-datos" className="hover:underline" data-testid="login-deletion-link">Eliminar mis datos</Link>
              </p>

              {/* Password Reset Overlay */}
              {resetStep && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute inset-0 bg-background/95 backdrop-blur-sm z-10 flex items-center justify-center p-4"
                >
                  <Card className="w-full max-w-md border-violet-500/20">
                    <CardContent className="p-6 space-y-5">
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                          <KeyRound className="w-8 h-8 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
                        </div>
                        <h2 className="text-xl font-bold">
                          {resetStep === 'request' ? 'Recuperar Contraseña' : 'Verificar Código'}
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                          {resetStep === 'request'
                            ? 'Ingresa tu correo y te enviaremos un código'
                            : `Ingresa el código enviado a ${resetEmail}`
                          }
                        </p>
                      </div>

                      {resetStep === 'request' ? (
                        <form onSubmit={handleRequestReset} className="space-y-4">
                          <div>
                            <Label>Correo electrónico</Label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                              <Input
                                data-testid="reset-email"
                                type="email"
                                value={resetEmail}
                                onChange={e => setResetEmail(e.target.value)}
                                className="pl-10"
                                placeholder="tu@email.com"
                              />
                            </div>
                          </div>
                          <div>
                            <Label>Método de envío</Label>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                              <button
                                type="button"
                                onClick={() => setResetMethod('email')}
                                className={cn(
                                  "flex items-center gap-2 p-3 rounded-lg border text-sm transition-all",
                                  resetMethod === 'email'
                                    ? "border-violet-500 bg-violet-500/5 text-brand-deep dark:text-brand-deep dark:text-brand-green"
                                    : "border-border hover:border-muted-foreground/30"
                                )}
                              >
                                <Mail className="w-4 h-4" /> Email
                              </button>
                              <button
                                type="button"
                                onClick={() => setResetMethod('sms')}
                                className={cn(
                                  "flex items-center gap-2 p-3 rounded-lg border text-sm transition-all",
                                  resetMethod === 'sms'
                                    ? "border-violet-500 bg-violet-500/5 text-brand-deep dark:text-brand-deep dark:text-brand-green"
                                    : "border-border hover:border-muted-foreground/30"
                                )}
                              >
                                <Smartphone className="w-4 h-4" /> SMS
                              </button>
                            </div>
                          </div>
                          <Button data-testid="reset-request-btn" type="submit" className="w-full bg-primary" disabled={loading}>
                            {loading ? 'Enviando...' : 'Enviar Código'}
                          </Button>
                          <button
                            type="button"
                            className="w-full text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1"
                            onClick={() => { setResetStep(null); setDebugCode(null); }}
                          >
                            <ArrowLeft className="w-3 h-3" /> Volver al login
                          </button>
                        </form>
                      ) : (
                        <form onSubmit={handleVerifyReset} className="space-y-4">
                          {debugCode && (
                            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                              <p className="text-xs text-amber-600 text-center">
                                Modo desarrollo - Código: <span className="font-mono font-bold text-lg">{debugCode}</span>
                              </p>
                            </div>
                          )}
                          <div>
                            <Label>Código de verificación</Label>
                            <Input
                              data-testid="reset-code"
                              value={resetCode}
                              onChange={e => setResetCode(e.target.value)}
                              placeholder="123456"
                              className="text-center text-2xl font-mono tracking-[0.5em]"
                              maxLength={6}
                            />
                          </div>
                          <div>
                            <Label>Nueva contraseña</Label>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                              <Input
                                data-testid="reset-new-password"
                                type={showPassword ? 'text' : 'password'}
                                value={resetNewPassword}
                                onChange={e => setResetNewPassword(e.target.value)}
                                className="pl-10 pr-10"
                                placeholder="Mínimo 6 caracteres"
                              />
                              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          <Button data-testid="reset-verify-btn" type="submit" className="w-full bg-primary" disabled={loading}>
                            {loading ? 'Verificando...' : 'Cambiar Contraseña'}
                          </Button>
                          <div className="flex justify-between">
                            <button
                              type="button"
                              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                              onClick={() => setResetStep('request')}
                            >
                              <ArrowLeft className="w-3 h-3" /> Reenviar código
                            </button>
                            <button
                              type="button"
                              className="text-sm text-muted-foreground hover:text-foreground"
                              onClick={() => { setResetStep(null); setDebugCode(null); }}
                            >
                              Cancelar
                            </button>
                          </div>
                        </form>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
